import { gql } from '@apollo/client';
import _ from 'lodash';
import React from 'react';
import type { BreadcrumbsProps } from '../components/Breadcrumbs';
import Breadcrumbs from '../components/Breadcrumbs';
import type { TopicBreadcrumbs_TopicsFragment } from '../graphql/generated/graphqlTypes';
import topicPath, { publicTopicPath } from '../utils/url/topicPath';
import useIsPublicView from '../utils/useIsPublicView';
import styles from './TopicBreadcrumbs.module.scss';
import TopicNameWithIcon from './TopicNameWithIcon';

const TOPICS_FRAGMENT = gql`
  fragment TopicBreadcrumbs_topics on PublicTopicFields {
    name
    ...topicPath_topic
    ...TopicNameWithIcon_topic
  }
  ${topicPath.fragments.topic}
  ${TopicNameWithIcon.fragments.topic}
`;

interface TopicBreadcrumbsProps {
  link?: boolean;
  names?: React.ReactNode[];
  showRoot?: boolean;
  showIcon?: boolean;
  size?: BreadcrumbsProps['size'];
  tab?: 'subtopics';
  topics: TopicBreadcrumbs_TopicsFragment[];
  truncate?: BreadcrumbsProps['truncate'];
}

const TopicBreadcrumbs = ({
  link = false,
  names = [],
  showRoot = false,
  showIcon = false,
  size,
  tab = undefined,
  topics,
  truncate,
}: TopicBreadcrumbsProps) => {
  const isPublicView = useIsPublicView();
  const items = _.compact(
    _.zip(topics, names)
      .reverse()
      .map(([topic, name]) => {
        if (!topic) return null;

        const finalName = name ?? topic.name;
        const item: BreadcrumbsProps['items'][number] = {
          name: showIcon ? (
            <TopicNameWithIcon name={finalName} topic={topic} />
          ) : (
            finalName
          ),
        };

        if (showIcon) {
          item.name = (
            <TopicNameWithIcon name={name ?? topic.name} topic={topic} />
          );
        }

        if (link) {
          item.to = isPublicView
            ? publicTopicPath(topic)
            : topicPath(topic, tab);
        }

        return item;
      }),
  );

  if (showRoot) {
    items.unshift({ name: 'Content Map', to: link ? '/topics' : undefined });
  }

  return (
    <div className={styles.container}>
      <Breadcrumbs items={items} size={size} truncate={truncate} />
    </div>
  );
};

TopicBreadcrumbs.fragments = {
  topics: TOPICS_FRAGMENT,
};

export default TopicBreadcrumbs;
